import React, { useMemo } from 'react';
import { View, Pressable, Image, StyleSheet, Platform, ActivityIndicator } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import ToolAvatar from '@/components/Tools/ToolAvatar';
import {
  OUTPUT_TYPE_ICON,
  formatTimeAgo,
  getFileUrl,
  getPaletteForTool,
  getRunStatusVisual,
  summarizeRunInputs,
} from '@/components/Tools/toolDetailHelpers';

/**
 * Slim table-row variant of a tool run, used by the history page when the
 * user switches to "list" view. Designed to be ~72px tall so a long history
 * scans at glance like a list of files rather than a wall of cards.
 *
 * Layout (left → right):
 *   1. 48×48 thumbnail (image preview, audio waveform glyph, or tool icon).
 *   2. Tool name + relative timestamp stacked.
 *   3. Status pill — only shown when the run is *not* completed, so the
 *      common case stays visually quiet.
 *   4. Delete button — always visible so it can't be missed or "fall off"
 *      the hover hit-box mid-click.
 */
function ToolHistoryRow({
  run,
  isDeleting = false,
  onOpen,
  onDelete,
}) {
  const { colors } = useTheme();
  const { t } = useTranslation();

  const tool    = run?.tool || null;
  const palette = useMemo(() => getPaletteForTool(tool), [tool]);
  const status  = useMemo(() => getRunStatusVisual(run?.status, colors, palette, t), [run?.status, colors, palette, t]);

  const firstFile = Array.isArray(run?.resultFiles) ? run.resultFiles[0] : null;
  const fileUrl   = getFileUrl(firstFile);
  const mime      = firstFile?.mimeType || '';
  const isImage   = mime.startsWith('image/');
  const isAudio   = mime.startsWith('audio/');
  const isVideo   = mime.startsWith('video/');

  const fallbackIcon = OUTPUT_TYPE_ICON[tool?.outputType] || tool?.icon || 'wand-magic-sparkles';
  const isCompleted  = run?.status === 'completed';
  const summary      = useMemo(() => summarizeRunInputs(run), [run]);

  const styles = useMemo(() => StyleSheet.create({
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 14,
      paddingVertical: 10,
      paddingHorizontal: 12,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
      minHeight: 72,
      ...(Platform.OS === 'web' ? {
        cursor: 'pointer',
        transition: 'border-color 0.15s ease, background-color 0.15s ease',
      } : null),
    },
    rowHovered: {
      borderColor: palette.fg,
      backgroundColor: colors.solid_color_secondary,
    },
    thumb: {
      width: 48,
      height: 48,
      borderRadius: 10,
      overflow: 'hidden',
      backgroundColor: palette.soft,
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    thumbImage: { width: '100%', height: '100%' },
    body: {
      flex: 1,
      minWidth: 0,
      gap: 2,
    },
    titleRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 7,
    },
    toolName: { flexShrink: 1 },
    timeAgo: { flexShrink: 0, marginLeft: 'auto' },
    summary: { lineHeight: 17 },
    toolIconWrap: {
      width: 18,
      height: 18,
      borderRadius: 5,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: palette.bg,
      flexShrink: 0,
    },
    statusChip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      paddingVertical: 3,
      paddingHorizontal: 8,
      borderRadius: 999,
      backgroundColor: colors.solid_color_primary,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      flexShrink: 0,
    },
    deleteBtn: {
      width: 30,
      height: 30,
      borderRadius: 8,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'transparent',
      flexShrink: 0,
      ...(Platform.OS === 'web' ? {
        cursor: 'pointer',
        transition: 'background-color 0.15s ease',
      } : null),
    },
    deleteBtnHover: {
      backgroundColor: `${colors.color_destructive}1F`,
    },
  }), [colors, palette]);

  return (
    <Pressable
      onPress={() => onOpen?.(run)}
      accessibilityRole='button'
      accessibilityLabel={`Open ${tool?.name || 'tool'} run from ${formatTimeAgo(run?.createdAt)}`}
      style={({ hovered }) => [styles.row, hovered && styles.rowHovered]}
    >
      <View style={styles.thumb}>
        {isImage && fileUrl ? (
          <Image source={{ uri: fileUrl }} style={styles.thumbImage} resizeMode='cover' />
        ) : isAudio ? (
          <Icon name='waveform-lines' size={20} color={palette.fg} />
        ) : isVideo ? (
          <Icon name='video' size={18} color={palette.fg} solid />
        ) : (
          <Icon name={fallbackIcon} size={18} color={palette.fg} solid />
        )}
      </View>

      <View style={styles.body}>
        <View style={styles.titleRow}>
          <ToolAvatar
            tool={tool}
            size={18}
            iconSize={9}
            borderRadius={5}
            backgroundColor={palette.bg}
            color={palette.fg}
            fallbackIcon={fallbackIcon}
            solid
            style={{ flexShrink: 0 }}
          />
          <Text size='s' weight='bold' color='primary' numberOfLines={1} style={styles.toolName}>
            {tool?.name || run?.toolSlug}
          </Text>
          <Text size='xs' color='tertiary' numberOfLines={1} style={styles.timeAgo}>
            {formatTimeAgo(run?.createdAt)}
          </Text>
        </View>
        {summary ? (
          <Text size='xs' color='secondary' numberOfLines={1} style={styles.summary}>
            {summary}
          </Text>
        ) : (
          <Text size='xs' color='tertiary' numberOfLines={1} style={styles.summary}>
            {!!tool?.category?.name && tool.category.name}
            {run?.creditCost != null && Number(run.creditCost) > 0
              ? `${tool?.category?.name ? ' · ' : ''}${run.creditCost} ${run.creditCost === 1 ? 'credit' : 'credits'}`
              : ''}
          </Text>
        )}
      </View>

      {!isCompleted && (
        <View style={styles.statusChip}>
          <Icon name={status.icon} size={9} color={status.color} solid />
          <Text size='xxs' weight='bold' style={{ color: status.color }}>
            {status.label}
          </Text>
        </View>
      )}

      {!!onDelete && (
        <Pressable
          onPress={(e) => {
            e?.stopPropagation?.();
            onDelete(run);
          }}
          disabled={isDeleting}
          style={({ hovered: btnHovered }) => [
            styles.deleteBtn,
            btnHovered && styles.deleteBtnHover,
          ]}
          accessibilityLabel={t('pages.tools.deleteRun') || 'Delete run'}
        >
          {isDeleting
            ? <ActivityIndicator size='small' color={colors.text_color_secondary} />
            : <Icon name='trash' size={12} color={colors.text_color_secondary} />}
        </Pressable>
      )}
    </Pressable>
  );
}

export default React.memo(ToolHistoryRow);
